import {PlusCircleIcon, XCircleIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import type React from 'react'
import type {FC} from 'react'
import {useCallback, useMemo, useState} from 'react'
import {getAvailableOrgProperties} from '../../../services/api'
import type {
  ConditionParameters,
  OrganizationPropertyConfiguration,
  OrganizationPropertyParameters,
  PropertyDescriptor,
} from '@github-ui/repos-rules-types/rules-types'
import {TargetsTable, type IncludeExcludeType} from '../TargetsTable'
import {AddPropertyDialog} from './AddPropertyDialog'

import styles from './OrganizationPropertyTarget.module.css'
import type {Enterprise} from '@github-ui/repos-types'
import {encodePart} from '@github-ui/paths'

export interface OrganizationPropertyTargetProps {
  enterprise: Enterprise
  parameters: OrganizationPropertyParameters
  readOnly: boolean
  updateParameters: (parameters: ConditionParameters) => void
  headerRowText?: string
  blankslate: {
    heading: string
    description?: React.ReactNode
  }
}

const INCLUDE_EXCLUDE_VALUES: IncludeExcludeType[] = ['include', 'exclude']

export const OrganizationPropertyTarget: FC<OrganizationPropertyTargetProps> = ({
  enterprise,
  readOnly,
  parameters,
  updateParameters,
  headerRowText,
  blankslate,
}) => {
  const [selectedActionType, setSelectedActionType] = useState<IncludeExcludeType>('include')
  const [addPropertyVisible, setAddPropertyVisible] = useState(false)
  const [properties, setProperties] = useState<PropertyDescriptor[]>([])
  const [arePropertiesFetched, setPropertiesFetched] = useState(false)

  const onActionSelect = async (actionType: IncludeExcludeType) => {
    setSelectedActionType(actionType)
    setAddPropertyVisible(true)
    if (!arePropertiesFetched) {
      const result = await getAvailableOrgProperties(
        `/enterprises/${encodePart(enterprise.ownerLogin)}/settings/ruleset-available-org-properties`,
      )
      setProperties(result.properties)
      setPropertiesFetched(true)
    }
  }

  const addProperty = (name: string, values: string[]) => {
    if (readOnly || name.length === 0 || values.length === 0) {
      return
    }

    switch (selectedActionType) {
      case 'include':
        if (!parameters.include.some(p => isSameProperty(name, p))) {
          updateParameters({
            ...parameters,
            include: [...parameters.include, {name, property_values: values}],
          })
        }
        break
      case 'exclude':
        if (!parameters.exclude.some(p => isSameProperty(name, p))) {
          updateParameters({
            ...parameters,
            exclude: [...parameters.exclude, {name, property_values: values}],
          })
        }
        break
    }
  }

  const removeProperty = useCallback(
    (type: IncludeExcludeType, name: string) => {
      if (readOnly || !name) {
        return
      }

      switch (type) {
        case 'include':
          updateParameters({
            ...parameters,
            include: parameters.include.filter(p => !isSameProperty(name, p)),
          })
          break
        case 'exclude':
          updateParameters({
            ...parameters,
            exclude: parameters.exclude.filter(p => !isSameProperty(name, p)),
          })
          break
      }
    },
    [parameters, readOnly, updateParameters],
  )

  const targets = useMemo(() => {
    function toTargetItem(property: OrganizationPropertyConfiguration, type: IncludeExcludeType) {
      const value = property.property_values.join(', ')

      const prefix = `props.${property.name}`

      return {
        type,
        prefix,
        value,
        display: property.property_values,
        onRemove: () => removeProperty(type, property.name),
      }
    }

    return [
      ...parameters.include.map(p => toTargetItem(p, 'include')),
      ...parameters.exclude.map(p => toTargetItem(p, 'exclude')),
    ]
  }, [parameters, removeProperty])

  const filteredProperties: PropertyDescriptor[] = properties.filter(p => {
    const existingProperties = selectedActionType === 'include' ? parameters.include : parameters.exclude
    return !existingProperties.some(ep => isSameProperty(p.name, ep))
  })

  return (
    <>
      <TargetsTable
        renderTitle={() => <h3 className="Box-title">Target by organization properties</h3>}
        renderAction={() => (
          <ActionMenu>
            <ActionMenu.Button>Add target</ActionMenu.Button>

            <ActionMenu.Overlay width="medium">
              <ActionList>
                {INCLUDE_EXCLUDE_VALUES.map(includeOrExclude => (
                  <ActionList.Item key={includeOrExclude} onSelect={() => onActionSelect(includeOrExclude)}>
                    <ActionList.LeadingVisual>
                      {includeOrExclude === 'include' ? (
                        <Octicon icon={PlusCircleIcon} className={styles.OrganizationPropertyIncludeIcon} />
                      ) : (
                        <Octicon icon={XCircleIcon} className={styles.OrganizationPropertyExcludeIcon} />
                      )}
                    </ActionList.LeadingVisual>
                    {includeOrExclude === 'include' ? 'Include by property' : 'Exclude by property'}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
        blankslate={blankslate}
        headerRowText={headerRowText}
        targets={targets}
        readOnly={readOnly}
      />
      {readOnly || !addPropertyVisible ? null : (
        <AddPropertyDialog
          properties={filteredProperties}
          onAdd={(name, _source, values) => {
            addProperty(name, values)
            setAddPropertyVisible(false)
          }}
          onClose={() => setAddPropertyVisible(false)}
          includeOrExclude={selectedActionType}
          target="organization"
        />
      )}
    </>
  )
}

function isSameProperty(name: string, property: OrganizationPropertyConfiguration) {
  return property.name === name
}
